import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Divider } from '@material-ui/core';
import MaterialGrid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Field, FieldArray, Form, getFormValues, reduxForm } from 'redux-form';
import { isMobile } from 'react-device-detect';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import GenericConfirmationCard from 'common/components/cards/genericConfirmationModel/genericConfirmationModel';
import { InputLabelOutlined } from 'common/components/fields/InputLabelOutlined';
import { InputSwitchLarge } from 'common/components/fields/InputSwitchLarge';
import { SelectLabel } from 'common/components/fields/SelectLabel';
import { FORM_RULES } from 'helpers/validations';
import { DEFAULT_SARAU_MODALITY, DEFAULT_SPORT_MODALITY } from '../config';

const selectionTypeOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'double', label: 'Dupla' },
  { value: 'team', label: 'Equipe' },
];

const specialTypeOptions = [
  { value: '', label: 'Padrão' },
  { value: 'opt_out', label: 'Opção de não participação' },
];

const sectionTitleStyle = {
  fontSize: 14,
  fontWeight: '600',
  marginBottom: 12,
  color: '#1a3a5c',
};

const helperBoxStyle = {
  backgroundColor: '#f5f7fa',
  borderRadius: 8,
  padding: 16,
  marginBottom: 20,
};

function renderSports({ fields }) {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography style={sectionTitleStyle}>Modalidades esportivas</Typography>
        <PrimaryButton
          type="button"
          onClick={() =>
            fields.push({
              ...DEFAULT_SPORT_MODALITY,
              shirt: { ...DEFAULT_SPORT_MODALITY.shirt },
              categories: { ...DEFAULT_SPORT_MODALITY.categories },
              partner_name: { ...DEFAULT_SPORT_MODALITY.partner_name },
            })
          }>
          Adicionar modalidade
        </PrimaryButton>
      </Box>

      {fields.length === 0 ? (
        <Box style={helperBoxStyle}>
          <Typography style={{ fontSize: 13, color: '#666' }}>
            Nenhuma modalidade esportiva configurada.
          </Typography>
        </Box>
      ) : null}

      {fields.map((field, index) => (
        <Box key={field} style={{ ...helperBoxStyle, marginTop: index === 0 ? 0 : 16 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography style={{ fontWeight: 600 }}>Esporte {index + 1}</Typography>
            <SecondaryButton type="button" color="primary" onClick={() => fields.remove(index)}>
              Remover
            </SecondaryButton>
          </Box>

          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Identificador"
                name={`${field}.id`}
                validate={[FORM_RULES.required]}
                helperText="Ex.: corrida_5km, beach_tennis"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Nome da modalidade"
                name={`${field}.name`}
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={SelectLabel}
                label="Tipo de participação"
                name={`${field}.selection_type`}
                options={selectionTypeOptions}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={SelectLabel}
                label="Tipo especial"
                name={`${field}.special_type`}
                options={specialTypeOptions}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.active`}
                label="Modalidade ativa"
                firstState="Ativa"
                secondState="Inativa"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.shirt.enabled`}
                label="Solicitar tamanho de camisa"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.shirt.required`}
                label="Camisa obrigatória"
                firstState="Obrigatório"
                secondState="Opcional"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12}>
              <Field
                component={InputLabelOutlined}
                label="Tamanhos de camisa"
                name={`${field}.shirt.sizes`}
                helperText="Informe separados por vírgula. Ex.: P, M, G, GG"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.categories.enabled`}
                label="Possui categorias"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.categories.required`}
                label="Categoria obrigatória"
                firstState="Obrigatório"
                secondState="Opcional"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.categories.multiple`}
                label="Permite múltiplas categorias"
                firstState="Múltipla"
                secondState="Única"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={8}>
              <Field
                component={InputLabelOutlined}
                label="Lista de categorias"
                name={`${field}.categories.options`}
                helperText="Informe separadas por vírgula. Ex.: Masculino, Feminino, Misto"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Máximo de categorias selecionáveis"
                name={`${field}.categories.max_selection`}
                type="number"
                helperText="Aplicável quando 'Permite múltiplas' está ativo. Padrão: 2."
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.partner_name.enabled`}
                label="Solicitar nome do parceiro"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.partner_name.required`}
                label="Parceiro obrigatório"
                firstState="Obrigatório"
                secondState="Opcional"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Rótulo do campo parceiro"
                name={`${field}.partner_name.label`}
              />
            </MaterialGrid>
          </MaterialGrid>
        </Box>
      ))}
    </Box>
  );
}

function renderSarau({ fields }) {
  return (
    <Box mt={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography style={sectionTitleStyle}>Atividades do sarau</Typography>
        <PrimaryButton
          type="button"
          onClick={() =>
            fields.push({
              ...DEFAULT_SARAU_MODALITY,
              extra_text: { ...DEFAULT_SARAU_MODALITY.extra_text },
            })
          }>
          Adicionar atividade
        </PrimaryButton>
      </Box>

      {fields.length === 0 ? (
        <Box style={helperBoxStyle}>
          <Typography style={{ fontSize: 13, color: '#666' }}>
            Nenhuma atividade do sarau configurada.
          </Typography>
        </Box>
      ) : null}

      {fields.map((field, index) => (
        <Box key={field} style={{ ...helperBoxStyle, marginTop: index === 0 ? 0 : 16 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography style={{ fontWeight: 600 }}>Atividade {index + 1}</Typography>
            <SecondaryButton type="button" color="primary" onClick={() => fields.remove(index)}>
              Remover
            </SecondaryButton>
          </Box>

          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Identificador"
                name={`${field}.id`}
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Nome da atividade"
                name={`${field}.name`}
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={SelectLabel}
                label="Tipo especial"
                name={`${field}.special_type`}
                options={specialTypeOptions}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.active`}
                label="Atividade ativa"
                firstState="Ativa"
                secondState="Inativa"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.extra_text.enabled`}
                label="Campo de descrição extra"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.extra_text.required`}
                label="Descrição extra obrigatória"
                firstState="Obrigatório"
                secondState="Opcional"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Rótulo do campo extra"
                name={`${field}.extra_text.label`}
              />
            </MaterialGrid>
          </MaterialGrid>
        </Box>
      ))}
    </Box>
  );
}

class Modalities extends React.Component {
  onSubmit = (values) => {
    values.submitFromButton = true;
    this.props.onClickProgress(values);
  };

  render() {
    return (
      <>
        <GenericConfirmationCard
          open={this.props.openConfirmation}
          cancelClose={this.props.handleCancelClose}
          general={this.props.general}
          title={this.props.confirmationTitle}
          text={this.props.confirmationSubtitle}
          confirmButtonColor="primary"
          confirmButtonLabel={this.props.buttonConfirmationLabel}
          onClose={this.props.handleCancelClose}
          onClick={this.props.closeModal}
        />
        <Form role="form" onSubmit={this.props.handleSubmit(this.onSubmit)} noValidate>
          <div
            style={{
              overflowY: 'scroll',
              height: isMobile ? 'calc(100vh - 240px)' : 'calc(100vh - 355px)',
              overflowX: 'hidden',
              padding: '0 10px',
            }}>
            <div style={{ marginTop: isMobile ? 0 : 20 }}>
              <Typography
                style={{ fontSize: 16, fontWeight: '700', marginBottom: 16 }}
                variant="subtitle2">
                Modalidades e atividades culturais
              </Typography>
              <Typography style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                Defina as modalidades esportivas e as atividades culturais exibidas ao participante.
              </Typography>

              <MaterialGrid container spacing={2}>
                <MaterialGrid item xs={12} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label="Limite de modalidades por participante"
                    name="body_max_modalidades"
                    type="number"
                    validate={[FORM_RULES.required]}
                  />
                </MaterialGrid>
                <MaterialGrid item xs={12} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label="Limite de atividades do sarau por participante"
                    name="body_max_sarau"
                    type="number"
                    validate={[FORM_RULES.required]}
                  />
                </MaterialGrid>
              </MaterialGrid>

              <Divider style={{ margin: '20px 0' }} />

              <FieldArray name="copa_sarau_config.modalities.sports" component={renderSports} />

              <Divider style={{ margin: '24px 0' }} />

              <FieldArray name="copa_sarau_config.modalities.sarau" component={renderSarau} />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 10px',
              borderTop: '1px solid #eee',
            }}>
            <SecondaryButton type="button" onClick={this.props.prevClick}>
              Voltar
            </SecondaryButton>
            <PrimaryButton type="submit">Salvar e continuar</PrimaryButton>
          </div>
        </Form>
      </>
    );
  }
}

Modalities.propTypes = {
  buttonConfirmationLabel: PropTypes.any,
  closeModal: PropTypes.func,
  confirmationSubtitle: PropTypes.any,
  confirmationTitle: PropTypes.any,
  general: PropTypes.any,
  handleCancelClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  onClickProgress: PropTypes.func,
  openConfirmation: PropTypes.any,
  prevClick: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    formValues: getFormValues('contactsSurveysForm')(state),
    general: state.general,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'contactsSurveysForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  })(Modalities)
);
